import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendUp, MapPin, MagicWand, Printer } from "@phosphor-icons/react";
import RequestsOverview from "@/components/rentincrease/RequestsOverview";
import { Checkbox } from "@/components/ui/checkbox";
import { printPdfBlob } from "@/lib/printPdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatEUR, formatDate } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { StatusPill } from "@/components/rentincrease/riMeta";

const FILTERS = [["all", "Alle"], ["possible_now", "Jetzt möglich"], ["possible_from", "Demnächst"], ["no_data", "Vergleichsmiete fehlt"], ["no_potential", "Kein Spielraum"], ["excluded", "Ausgeschlossen"]];

export default function Mieterhoehungen() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [printing, setPrinting] = useState(false);
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const load = useCallback(() => api.get("/rent-increase/portfolio").then(({ data }) => setD(data)), []);
  const batch = async () => {
    setPrinting(true);
    try {
      const { data: job } = await api.post("/rent-increase/batch-print", { tenancy_ids: chosen });
      const tid = toast.loading(`Sammeldruck: 0 / ${job.total} Verlangen erzeugt…`);
      let j = job;
      while (j.status === "running") {
        await new Promise((r) => setTimeout(r, 1500));
        j = (await api.get(`/rent-increase/batch-print/${job.id}`)).data;
        toast.loading(`Sammeldruck: ${j.done} / ${j.total} Verlangen erzeugt…`, { id: tid });
      }
      toast.dismiss(tid);
      if (j.status === "error") throw new Error(j.error);
      const res = await api.get(`/rent-increase/batch-print/${job.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
      printPdfBlob(blob);
      toast.success(`${j.printed} Verlangen erzeugt und abgelegt – unterschriebene Scans bitte nachreichen (Aufgabe angelegt)`);
      if (j.skipped?.length) toast.warning(`${j.skipped.length} übersprungen: ${j.skipped.map((x) => `${x.contract_number}: ${x.reason}`).join(" · ")}`, { duration: 15000 });
      setSel({}); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Sammeldruck fehlgeschlagen");
    } finally { setPrinting(false); }
  };
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => (d?.rows || []).filter((r) => (f === "all" || r.status === f)
    && (!q || [r.property_name, r.property_number, r.contract_number, r.unit_number, r.tenant_names].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase()))), [d, f, q]);
  const s = d?.summary;
  const selectable = rows.filter((r) => ["possible_now", "possible_from"].includes(r.status));
  const allSel = selectable.length > 0 && selectable.every((r) => sel[r.tenancy_id]);
  const toggleAll = (v) => setSel((p) => ({ ...p, ...Object.fromEntries(selectable.map((r) => [r.tenancy_id, !!v])) }));
  const [tab, setTab] = useState("potential");
  const tabs = (
    <div className="flex gap-1 border-b border-slate-200" data-testid="ri-tabs">
      {[["potential", "Potenziale & Prüfung"], ["vorgaenge", `Laufende & abgeschlossene Mieterhöhungen${d?.summary?.running ? ` (${d.summary.running} laufend${d.summary.to_review ? ` · ${d.summary.to_review} Zustimmung prüfen` : ""})` : ""}`]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setTab(k)} data-testid={`ri-tab-${k}`}
          className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
      ))}
    </div>
  );
  if (tab === "vorgaenge") return (
    <div className="space-y-5" data-testid="mieterhoehungen-page">
      <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><TrendUp size={24} /> Mieterhöhungen</h1>
      {tabs}
      <RequestsOverview />
    </div>
  );
  return (
    <div className="space-y-5" data-testid="mieterhoehungen-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><TrendUp size={24} /> Mieterhöhungen</h1>
          <p className="text-sm text-slate-500">Prüfung aller laufenden Wohnraum-Mietverträge auf Erhöhung zur ortsüblichen Vergleichsmiete (§ 558 BGB) — Sperrfrist, Kappungsgrenze, Vergleichsmiete.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link to="/vergleichsmieten"><Button size="sm" variant="outline" className="gap-1" data-testid="ri-areas-btn"><MapPin size={14} /> Vergleichsmieten je Stadt/Stadtteil</Button></Link>
          <Link to="/mieterhoehungen/assistent"><Button size="sm" className="gap-1" data-testid="ri-wizard-btn"><MagicWand size={14} /> Assistent starten</Button></Link>
        </div>
      </div>
      {tabs}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ri-summary">
          {[["Jetzt möglich", s.possible_now, "ri-sum-now"], ["Demnächst möglich", s.possible_from, "ri-sum-from"], ["Mehrertrag sofort / Monat", formatEUR(s.potential_now_eur_month), "ri-sum-eur-now"], ["Vergleichsmiete fehlt", s.no_data, "ri-sum-nodata"]].map(([l, v, t]) => (
            <div key={t} className="rounded-md border border-slate-200 p-3"><div className="text-xs text-slate-500">{l}</div><div className="text-xl font-semibold tabular-nums" data-testid={t}>{v}</div></div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF(k)} data-testid={`ri-filter-${k}`}
            className={`px-2.5 py-1 text-xs rounded border ${f === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700"}`}>{l}</button>
        ))}
        {chosen.length > 0 && <Button size="sm" className="gap-1 ml-auto" onClick={batch} disabled={printing} data-testid="ri-batch-print"><Printer size={14} /> {printing ? "Erzeuge…" : `${chosen.length} Verlangen drucken`}</Button>}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mieter, Objekt, Vertrag, Einheit…" className={`w-60 h-8 ${chosen.length ? "" : "ml-auto"}`} data-testid="ri-search" />
      </div>
      {!d ? <div className="text-sm text-slate-500">Prüfe Bestand…</div> : (
        <div className="border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="ri-table">
            <thead className="bg-slate-50 text-xs text-slate-500 text-left">
              <tr><th className="pl-3 w-8">{selectable.length > 0 && <Checkbox checked={allSel} onCheckedChange={toggleAll} title="Alle auswählbaren (gefiltert) auswählen" data-testid="ri-select-all" />}</th><th className="px-3 py-2">Mietvertrag</th><th>Objekt / Einheit</th><th>Status</th><th className="text-right">Miete</th><th className="text-right">€/m²</th><th className="text-right">Max. neu</th><th className="text-right">Mehr / Monat</th><th className="px-3">Wirksam ab</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tenancy_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ri-row-${r.tenancy_id}`}>
                  <td className="pl-3">{["possible_now", "possible_from"].includes(r.status) && <Checkbox checked={!!sel[r.tenancy_id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [r.tenancy_id]: !!v }))} data-testid={`ri-select-${r.tenancy_id}`} />}</td>
                  <td className="px-3 py-2"><Link to={`/mietverhaeltnisse/${r.tenancy_id}?tab=mieterhoehung`} className="text-teal-700 hover:underline font-mono text-xs">{r.contract_number}</Link>
                    {r.tenant_names && <div className="text-xs text-slate-700 truncate max-w-[220px]" title={r.tenant_names} data-testid={`ri-row-tenants-${r.tenancy_id}`}>{r.tenant_names}</div>}</td>
                  <td className="text-xs">{r.property_name}{r.unit_number ? ` · ${r.unit_number}` : ""}{r.unit_location ? ` · ${r.unit_location}` : ""}</td>
                  <td><StatusPill status={r.status} label={r.status_label} testId={`ri-row-status-${r.tenancy_id}`} /></td>
                  <td className="text-right tabular-nums">{formatEUR(r.current_rent)}</td>
                  <td className="text-right tabular-nums text-xs">{r.current_per_m2?.toFixed(2) ?? "—"}</td>
                  <td className="text-right tabular-nums">{r.increase_eur > 0 ? formatEUR(r.new_rent) : "—"}</td>
                  <td className="text-right tabular-nums font-medium text-emerald-700">{r.increase_eur > 0 ? `+${formatEUR(r.increase_eur)}` : "—"}</td>
                  <td className="px-3 text-xs">{r.increase_eur > 0 && r.earliest_effective_date ? formatDate(r.earliest_effective_date) : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="text-center text-sm text-slate-500 py-8">Keine Mietverträge für diesen Filter.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
